import { useMemo, useCallback } from 'react';
import { SConfirmDialog } from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { useQuotePricingCalculations } from 'pages/quotes/components/quote-items/components/product/hooks/use-quote-pricing-calculations';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import {
  DeleteIconV2,
  DocumentIconV2,
  RefreshIcon,
  MoveIcon,
  FillBagOutlineIcon,
  AddBySKUOutlineIcon,
  AddSPOOutlineIcon,
  AddSectionIcon,
  EditIcon
} from '@setvi/shared/components/sicons';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import {
  useQuoteData,
  useQuoteItemsData,
  useQuoteMutations,
  useQuoteStatusFlags
} from '../../../../context';
import { AddNoteDialog } from '../add-note-dialog';
import { EditLabelDialog } from '../edit-label-dialog';
import { AddBySku } from '../../../add-by-sku';
import { AddSPO } from '../../../add-spo';
import { AddFromCatalogDialog } from '../../../add-from-catalog-dialog';
import { SectionDialog } from '../../../add-section-dialog';

const MENU_ICONS = {
  refresh: <RefreshIcon fontSize="small" />,
  document: <DocumentIconV2 fontSize="small" />,
  edit: <EditIcon fontSize="small" />,
  options: (
    <AccountTreeOutlinedIcon fontSize="small" style={{ color: '#5D7083' }} />
  ),
  catalog: <FillBagOutlineIcon fontSize="small" />,
  sku: <AddBySKUOutlineIcon fontSize="small" />,
  spo: <AddSPOOutlineIcon fontSize="small" />,
  move: <MoveIcon fontSize="small" />,
  addSection: <AddSectionIcon fontSize="small" />,
  delete: <DeleteIconV2 fontSize="small" />
};

export const useProductMenuItems = (product: QuoteProductDTO) => {
  const { openDialog, closeDialog } = useDialog();
  const { quote, currentStatus } = useQuoteData();
  const { items: quoteItems } = useQuoteItemsData();
  const {
    updateItem,
    updateItemQuantity,
    updateItemNote,
    updateItemLabels,
    removeItem,
    moveItem,
    addSubitems,
    addFreehandSubitem,
    addSectionAndMoveItem,
    refreshSinglePrice
  } = useQuoteMutations();
  const { isAddingSubitems, isAddingFreehandSubitem } = useQuoteStatusFlags();

  const isQuoteEditable =
    !!currentStatus?.isQuoteEditable && !!quote?.acl?.canEditQuote;

  const productId = product?.id || product?._id;
  const segmentId = quote?.segment?._id;

  const parentSectionId = useMemo(() => {
    if (!quoteItems) return null;

    for (const item of quoteItems) {
      if (item.type === 'section' && 'children' in item) {
        const hasProduct = (item.children as QuoteProductDTO[])?.some(
          (child: QuoteProductDTO) => (child.id || child._id) === productId
        );
        if (hasProduct) {
          return item.id || item._id;
        }
      }
    }
    return null;
  }, [quoteItems, productId]);

  const availableSections = useMemo(
    () =>
      quoteItems
        ?.filter(item => {
          if (item.type !== 'section') return false;
          const sectionId = item.id || item._id;
          return sectionId !== parentSectionId;
        })
        .map(section => ({
          id: section.id || section._id,
          name: section.name || 'Unnamed Section'
        })) || [],
    [quoteItems, parentSectionId]
  );

  const handlePricingUpdate = useCallback(
    (itemID: string, updates: any) => {
      if ('quantity' in updates && Object.keys(updates).length === 1) {
        updateItemQuantity.mutate({
          itemId: itemID,
          value: updates.quantity
        });
      } else {
        updateItem.mutate({ itemId: itemID, updates });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    handleQuantityChange,
    handleSellChange,
    handleProfitMarginChange,
    handleProfitMarginPercentageChange
  } = useQuotePricingCalculations({ updatePricing: handlePricingUpdate });

  const handleOpenAddNoteDialog = useCallback(() => {
    openDialog(
      <AddNoteDialog
        itemId={productId}
        itemName={product.name}
        note={product?.note}
        onClose={() => closeDialog(`add-note-${productId}`)}
        onSave={(id, newNote) => {
          updateItemNote.mutate({ itemId: id, note: newNote });
        }}
      />,
      { id: `add-note-${productId}` }
    );
  }, [
    openDialog,
    closeDialog,
    productId,
    product.name,
    product?.note,
    updateItemNote
  ]);

  const handleLabelDialog = useCallback(() => {
    openDialog(
      <EditLabelDialog
        itemId={productId}
        labelType={product.labelType || null}
        customLabel={product.customLabel}
        onClose={() => closeDialog(`edit-label-${productId}`)}
        onSave={(id, data) => {
          updateItemLabels.mutate({
            itemId: id,
            labelType: data.labelType,
            customLabel: data.customLabel,
            includeInTotalCost: product.includeInTotalCost ?? true
          });
        }}
      />,
      { id: `edit-label-${productId}` }
    );
  }, [
    openDialog,
    closeDialog,
    productId,
    product.labelType,
    product.customLabel,
    product.includeInTotalCost,
    updateItemLabels
  ]);

  const handleAddToNewSection = useCallback(() => {
    openDialog(
      <SectionDialog
        onClose={() => closeDialog(`add-section-move-${productId}`)}
        addSection={params => {
          addSectionAndMoveItem({
            ...params,
            itemId: productId
          });
          closeDialog(`add-section-move-${productId}`);
        }}
      />,
      { id: `add-section-move-${productId}` }
    );
  }, [openDialog, closeDialog, productId, addSectionAndMoveItem]);

  const handleRemoveItem = useCallback(() => {
    openDialog(
      <SConfirmDialog
        type="delete"
        title="Remove Item from the Quote"
        description="This will permanently remove this item from the quote."
        additionalMessage="This action CANNOT be undone."
        onClose={() => closeDialog(`remove-item-${productId}`)}
        onConfirm={() => {
          removeItem(productId);
          closeDialog(`remove-item-${productId}`);
        }}
        confirmText="Remove"
        closeText="Cancel"
      />,
      { id: `remove-item-${productId}` }
    );
  }, [openDialog, closeDialog, productId, removeItem]);

  const handleAddFromCatalog = useCallback(() => {
    openDialog(
      <AddFromCatalogDialog
        onClose={() => closeDialog(`add-from-catalog-${productId}`)}
        parentId={productId}
        quote={quote as any}
        addProducts={({ products, parentId }) =>
          addSubitems({
            itemId: parentId || productId,
            products
          })
        }
      />,
      { id: `add-from-catalog-${productId}` }
    );
  }, [openDialog, closeDialog, productId, quote, addSubitems]);

  const handleAddBySku = useCallback(() => {
    openDialog(
      <AddBySku
        onClose={() => closeDialog(`add-by-sku-${productId}`)}
        addProducts={products =>
          addSubitems({
            itemId: productId,
            products: products.products
          })
        }
        isAddingProducts={isAddingSubitems}
        selectedCustomer={quote?.account as any}
        segmentId={segmentId}
      />,
      { id: `add-by-sku-${productId}` }
    );
  }, [
    openDialog,
    isAddingSubitems,
    quote?.account,
    segmentId,
    productId,
    closeDialog,
    addSubitems
  ]);

  const handleAddSpo = useCallback(() => {
    openDialog(
      <AddSPO
        onClose={() => closeDialog(`add-spo-${productId}`)}
        onSubmit={async spoData => {
          await addFreehandSubitem({
            itemId: productId,
            ...spoData
          });
          closeDialog(`add-spo-${productId}`);
        }}
        isAddingProducts={isAddingFreehandSubitem}
      />,
      { id: `add-spo-${productId}` }
    );
  }, [
    openDialog,
    closeDialog,
    productId,
    addFreehandSubitem,
    isAddingFreehandSubitem
  ]);

  const handleRefreshPrice = useCallback(() => {
    if (product?.sku) {
      refreshSinglePrice({
        productId,
        sku: product.sku
      });
    }
  }, [product?.sku, productId, refreshSinglePrice]);

  const menuItems = useMemo(() => {
    if (!isQuoteEditable) return [];

    return [
      {
        color: '#333D47',
        icon: MENU_ICONS.refresh,
        text: 'Refresh Price',
        disabled: product?.isFreeHand || !product?.sku,
        onClick: handleRefreshPrice
      },
      {
        color: '#333D47',
        icon: MENU_ICONS.document,
        text: product?.note?.trim() ? 'Edit Note' : 'Add Note',
        onClick: handleOpenAddNoteDialog
      },
      ...(product?.children?.length
        ? [
            {
              color: '#333D47',
              icon: MENU_ICONS.edit,
              text: product?.labelType ? 'Edit Label' : 'Add Label',
              onClick: handleLabelDialog
            }
          ]
        : []),
      {
        color: '#333D47',
        icon: MENU_ICONS.options,
        text: 'Add Options',
        submenu: [
          {
            icon: MENU_ICONS.catalog,
            text: 'Add From Catalog',
            color: '#333D47',
            onClick: handleAddFromCatalog
          },
          {
            icon: MENU_ICONS.sku,
            text: 'Add by SKU',
            color: '#333D47',
            onClick: handleAddBySku
          },
          {
            icon: MENU_ICONS.spo,
            text: 'Add SPO',
            color: '#333D47',
            onClick: handleAddSpo
          }
        ]
      },
      {
        color: '#333D47',
        icon: MENU_ICONS.move,
        text: 'Move to section',
        submenu: [
          ...availableSections.map(section => ({
            text: section.name,
            color: '#333D47',
            onClick: () => moveItem(productId, section.id)
          })),
          {
            icon: MENU_ICONS.addSection,
            text: 'Add to new section',
            color: '#333D47',
            onClick: handleAddToNewSection
          },
          ...(parentSectionId
            ? [
                {
                  text: 'No section (remove)',
                  color: '#333D47',
                  onClick: () => moveItem(productId, null)
                }
              ]
            : [])
        ]
      },
      {
        icon: MENU_ICONS.delete,
        color: 'danger',
        text: 'Remove Item',
        onClick: handleRemoveItem
      }
    ];
  }, [
    isQuoteEditable,
    product?.isFreeHand,
    product?.sku,
    product?.note,
    product?.children?.length,
    product?.labelType,
    handleRefreshPrice,
    handleOpenAddNoteDialog,
    handleLabelDialog,
    handleAddFromCatalog,
    handleAddBySku,
    handleAddSpo,
    availableSections,
    moveItem,
    productId,
    handleAddToNewSection,
    parentSectionId,
    handleRemoveItem
  ]);

  return {
    menuItems,
    isQuoteEditable,
    handleQuantityChange,
    handleSellChange,
    handleProfitMarginChange,
    handleProfitMarginPercentageChange
  };
};
